// A2 : catalogue (G08, charte D-038) : produits et densités avec date d'effet, densités encore à confirmer
// signalées en tête, modification d'une densité à côté du produit choisi, conversion d'une quantité,
// formats de bouteilles, catégories d'installations du référentiel, historique des densités.

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CATEGORIES_INSTALLATIONS, formaterNombre, formaterQuantite, UNITES, type Unite } from '@carburanttrack/shared';
import { api } from '../../api/client';
import { useProduits, type Produit } from '../../api/requetes';
import { aUnRole, useSession } from '../../app/session';
import { Message, MessageErreur, Squelette } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { montrerSurPetitEcran } from '../../composants/selection';
import { date, dateHeure, libelleCategorie } from '../../composants/format';
import { Jauge } from '../../composants/Visualisations';
import { comptesReferentiel, useSituation } from '../accueil/communs';

interface Conversion {
  id: string;
  produitCode: string;
  valeur: string;
  dateEffet: string;
  motif: string;
  source: string;
  auteurPrenom: string | null;
  auteurNom: string | null;
}

const symbole = (u: string) => (u === 'm3' ? 'm³' : u);

export function Catalogue() {
  const { etat } = useSession();
  const produits = useProduits();
  const formats = useQuery({ queryKey: ['formats'], queryFn: async () => (await api<{ code: string; libelle: string; contenuKg: string }[]>('/api/v1/formats-bouteilles')).donnees });
  const historique = useQuery({ queryKey: ['conversions'], queryFn: async () => (await api<Conversion[]>('/api/v1/conversions')).donnees });
  const situation = useSituation();
  const administrateur = aUnRole(etat, 'administrateur_national');
  const [choisi, setChoisi] = useState<string | null>(null);
  if (produits.isLoading) return <Squelette lignes={6} />;
  if (produits.error) return <MessageErreur erreur={produits.error} />;
  const liste = produits.data!;
  const selection = liste.find((p) => p.code === choisi) ?? liste[0] ?? null;
  // Densité encore « de référence initiale » : la dernière entrée en vigueur n'a pas d'auteur (initialisation).
  const maintenant = new Date();
  const initiale = (code: string) => {
    const enVigueur = (historique.data ?? []).filter((h) => h.produitCode === code && new Date(h.dateEffet) <= maintenant).sort((a, b) => b.dateEffet.localeCompare(a.dateEffet))[0];
    return enVigueur ? !enVigueur.auteurNom : null;
  };
  const aConfirmer = historique.data ? liste.filter((p) => p.densite && initiale(p.code)).length : 0;

  return (
    <div className="page-catalogue">
      <div className="page-tete">
        <div>
          <span className="contexte">Référentiel national</span>
          <h1>Catalogue</h1>
          <p>Produits, unités, densités de référence avec date d’effet, formats de bouteilles.</p>
        </div>
      </div>

      {aConfirmer > 0 && (
        <p className="note ambre">
          <Icone nom="incident" />
          <span>
            {aConfirmer} densité{aConfirmer > 1 ? 's sont des valeurs' : ' est une valeur'} de référence initiale{aConfirmer > 1 ? 's' : ''} (D-011), non réglementaire{aConfirmer > 1 ? 's' : ''}, à confirmer par les professionnels.
          </span>
        </p>
      )}

      <div className="deux-colonnes">
        <section className="principal carte-bloc" aria-labelledby="titre-produits">
          <div className="carte-bloc-tete">
            <h2 id="titre-produits">Produits</h2>
            <span className="attenue">{administrateur ? 'Choisissez une ligne pour modifier sa densité' : 'Choisissez une ligne pour l’utiliser dans la conversion'}</span>
          </div>
          <div className="tableau-conteneur">
            <table className="tableau lignes-48">
              <caption className="visuellement-cache">Produits du catalogue</caption>
              <thead>
                <tr>
                  <th scope="col">Produit</th>
                  <th scope="col">Unité</th>
                  <th scope="col" className="nombre">
                    Densité (kg par litre)
                  </th>
                  <th scope="col" className="masquable">
                    En vigueur depuis
                  </th>
                  <th scope="col" className="masquable">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody>
                {liste.map((p) => {
                  const init = p.densite ? initiale(p.code) : null;
                  return (
                    <tr key={p.code} aria-selected={selection?.code === p.code}>
                      <td>
                        <button type="button" className="bouton-ligne" aria-pressed={selection?.code === p.code} onClick={() => { setChoisi(p.code); montrerSurPetitEcran('titre-produit-choisi'); }}>
                          {p.nom}
                        </button>
                      </td>
                      <td>{symbole(p.uniteReference)}</td>
                      <td className="nombre valeur">{p.densite ? p.densite.valeur.replace('.', ',') : <span className="indisponible">indisponible</span>}</td>
                      <td className="masquable attenue">{p.densite ? date(p.densite.dateEffet) : 'indisponible'}</td>
                      <td className="masquable">{init === true ? <span className="etiquette ambre">À confirmer</span> : init === false ? <span className="etiquette">Modifiée</span> : null}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        {selection && (
          <aside className="lateral" aria-labelledby="titre-produit-choisi">
            <section className="carte-bloc">
              <span className="sur-titre">{administrateur ? 'Modifier une densité' : 'Produit choisi'}</span>
              <h2 id="titre-produit-choisi">{selection.nom}</h2>
              <p className="attenue">
                Valeur actuelle : <b>{selection.densite ? `${selection.densite.valeur.replace('.', ',')} kg par litre` : 'indisponible'}</b>
                {selection.densite && <small className="bloc">{selection.densite.motif}</small>}
              </p>
              {administrateur ? <FormulaireDensite key={selection.code} produit={selection} /> : <p className="attenue">La modification d’une densité est réservée à l’administrateur national.</p>}
            </section>
          </aside>
        )}
      </div>

      <div className="grille-2">
        <section className="carte-bloc" aria-labelledby="titre-conversion">
          <h2 id="titre-conversion">Convertir une quantité</h2>
          <Convertisseur key={selection?.code} produitInitial={selection?.code ?? ''} produits={liste} formats={formats.data ?? []} />
        </section>
        <div>
          <section className="carte-bloc" aria-labelledby="titre-formats">
            <h2 id="titre-formats">Formats de bouteilles GPL</h2>
            <div className="grille-cartes">
              {formats.data?.map((f) => (
                <div key={f.code}>
                  <b className="valeur grand-chiffre">{formaterQuantite(f.contenuKg, 'kg', { decimales: 1 })}</b>
                  <span className="libelle">{f.libelle}, contenu nominal</span>
                </div>
              ))}
            </div>
            <p className="attenue">Un nombre de bouteilles n’est jamais présenté comme un volume de gaz.</p>
          </section>
          {situation.data?.installationsLisibles && <CategoriesInstallations situation={situation.data} />}
        </div>
      </div>

      <section className="carte-bloc" aria-labelledby="titre-historique">
        <h2 id="titre-historique">Historique des densités</h2>
        {historique.data && (
          <div className="tableau-conteneur">
            <table className="tableau">
              <thead>
                <tr>
                  <th scope="col">Produit</th>
                  <th scope="col" className="nombre">
                    Densité
                  </th>
                  <th scope="col">Date d’effet</th>
                  <th scope="col" className="masquable">
                    Motif
                  </th>
                  <th scope="col" className="masquable">
                    Auteur
                  </th>
                </tr>
              </thead>
              <tbody>
                {historique.data.map((h) => (
                  <tr key={h.id}>
                    <td>{liste.find((p) => p.code === h.produitCode)?.nom ?? h.produitCode}</td>
                    <td className="nombre">{h.valeur.replace('.', ',')}</td>
                    <td>
                      {dateHeure(h.dateEffet)}
                      {new Date(h.dateEffet) > maintenant && <span className="etiquette ambre">programmée</span>}
                    </td>
                    <td className="masquable">{h.motif}</td>
                    <td className="masquable attenue">{h.auteurNom ? `${h.auteurPrenom} ${h.auteurNom}` : 'initialisation du système'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function CategoriesInstallations({ situation }: { situation: NonNullable<ReturnType<typeof useSituation>['data']> }) {
  const c = comptesReferentiel(situation, null);
  const lignes = CATEGORIES_INSTALLATIONS.map((cat) => ({ cat, n: c.parCategorie(cat) })).filter((x) => x.n > 0);
  return (
    <section className="carte-bloc" aria-labelledby="titre-categories-catalogue">
      <h2 id="titre-categories-catalogue">Catégories d’installations</h2>
      {lignes.map(({ cat, n }) => (
        <Jauge key={cat} libelle={libelleCategorie(cat)} part={c.total > 0 ? (n / c.total) * 100 : null} valeur={formaterNombre(n)} motifIndisponible="aucune installation" />
      ))}
      <p className="attenue espace-haut">
        {formaterNombre(c.total)} installation{c.total > 1 ? 's' : ''} dans le référentiel de votre périmètre.
      </p>
    </section>
  );
}

function FormulaireDensite({ produit }: { produit: Produit }) {
  const client = useQueryClient();
  const [valeur, setValeur] = useState('');
  const [dateEffet, setDateEffet] = useState('');
  const [motif, setMotif] = useState('');
  const envoi = useMutation({
    mutationFn: () =>
      api('/api/v1/conversions', {
        corps: { produitCode: produit.code, parametre: 'densite_kg_par_litre', valeur: valeur.replace(',', '.'), motif, ...(dateEffet ? { dateEffet: new Date(dateEffet).toISOString() } : {}) },
      }),
    onSuccess: async () => {
      setValeur('');
      setMotif('');
      await client.invalidateQueries({ queryKey: ['produits'] });
      await client.invalidateQueries({ queryKey: ['conversions'] });
    },
  });
  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        envoi.mutate();
      }}
    >
      <div className="champ">
        <label htmlFor="d-valeur">Nouvelle densité (kg par litre)</label>
        <input id="d-valeur" inputMode="decimal" placeholder={produit.densite?.valeur.replace('.', ',')} value={valeur} onChange={(e) => setValeur(e.target.value)} />
      </div>
      <div className="champ">
        <label htmlFor="d-date">Date d’effet</label>
        <input id="d-date" type="datetime-local" aria-describedby="d-date-aide" value={dateEffet} onChange={(e) => setDateEffet(e.target.value)} />
        <span id="d-date-aide" className="aide">
          Vide : effet immédiat.
        </span>
      </div>
      <div className="champ">
        <label htmlFor="d-motif">Motif (obligatoire)</label>
        <textarea id="d-motif" rows={3} placeholder="Référence de la note ou de la mesure" value={motif} onChange={(e) => setMotif(e.target.value)} />
      </div>
      {envoi.error && <MessageErreur erreur={envoi.error} titre="Modification refusée" />}
      {envoi.isSuccess && <Message type="succes">Densité enregistrée avec sa date d’effet.</Message>}
      <button type="submit" className="bouton primaire pleine-largeur" disabled={!valeur || motif.trim().length < 3 || envoi.isPending}>
        Enregistrer la densité
      </button>
      <p className="attenue espace-haut">L’ancienne valeur reste dans l’historique et l’opération est inscrite au journal d’audit.</p>
    </form>
  );
}

function Convertisseur({ produits, formats, produitInitial }: { produits: Produit[]; formats: { code: string; libelle: string }[]; produitInitial: string }) {
  const [quantite, setQuantite] = useState('');
  const [de, setDe] = useState<Unite>('L');
  const [vers, setVers] = useState<Unite>('m3');
  const [produit, setProduit] = useState(produitInitial);
  const [format, setFormat] = useState('');
  const [resultat, setResultat] = useState<{ ok: boolean; valeur?: string; motif?: string; exacte?: boolean } | null>(null);
  const [erreur, setErreur] = useState<unknown>(null);
  async function convertir(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      const p = new URLSearchParams({ quantite: quantite.replace(',', '.'), de, vers });
      if (produit) p.set('produit', produit);
      if (format) p.set('format', format);
      setResultat((await api<{ ok: boolean; valeur?: string; motif?: string; exacte?: boolean }>(`/api/v1/conversion?${p.toString()}`)).donnees);
    } catch (err) {
      setErreur(err);
    }
  }
  return (
    <form onSubmit={convertir}>
      <div className="formulaire-ligne serre">
        <div className="champ">
          <label htmlFor="c-q">Quantité</label>
          <input id="c-q" inputMode="decimal" value={quantite} onChange={(e) => setQuantite(e.target.value)} />
        </div>
        <div className="champ">
          <label htmlFor="c-de">De</label>
          <select id="c-de" value={de} onChange={(e) => setDe(e.target.value as Unite)}>
            {UNITES.map((u) => (
              <option key={u} value={u}>
                {symbole(u)}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="c-vers">Vers</label>
          <select id="c-vers" value={vers} onChange={(e) => setVers(e.target.value as Unite)}>
            {UNITES.map((u) => (
              <option key={u} value={u}>
                {symbole(u)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="formulaire-ligne">
        <div className="champ">
          <label htmlFor="c-produit">Produit (densité)</label>
          <select id="c-produit" value={produit} onChange={(e) => setProduit(e.target.value)}>
            <option value="">Aucun</option>
            {produits.map((p) => (
              <option key={p.code} value={p.code}>
                {p.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="c-format">Format de bouteille</label>
          <select id="c-format" value={format} onChange={(e) => setFormat(e.target.value)}>
            <option value="">Aucun</option>
            {formats.map((f) => (
              <option key={f.code} value={f.code}>
                {f.libelle}
              </option>
            ))}
          </select>
        </div>
      </div>
      <button type="submit" className="bouton primaire" disabled={!quantite}>
        Convertir
      </button>
      {erreur !== null && <MessageErreur erreur={erreur} />}
      <div className="resultat-conversion" aria-live="polite">
        <span className="libelle">Résultat</span>
        {resultat === null ? (
          <span className="indisponible">aucune conversion demandée</span>
        ) : resultat.ok ? (
          <>
            <b className="valeur">{formaterQuantite(resultat.valeur!, vers, { decimales: 3 })}</b>
            {!resultat.exacte && <span className="attenue">valeur arrondie</span>}
          </>
        ) : (
          <span className="indisponible">
            conversion refusée
            <span className="motif">{resultat.motif}</span>
          </span>
        )}
      </div>
    </form>
  );
}
